import { Button } from "@/components/ui/button";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import accessibilityLogo from "@/assets/accessibility.svg";
import cookieLogo from "@/assets/cookie.svg";
import logoWhite from "@/assets/firstgivingwhite.webp";
import trustpilotLogo from "@/assets/trustpilot-white.webp";
import gdprLogo from "@/assets/gdpr.webp";
import iso9001Logo from "@/assets/iso9001.webp";
import sectigoLogo from "@/assets/sectigo.webp";
import bcorpLogo from "@/assets/bcorp-white.webp";

export const Footer = () => {
  const navigate = useNavigate();
  
  return (
    <footer className="bg-[#123421] text-white py-8 sm:py-12 md:py-16">
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-12 md:mb-16">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <img src={logoWhite} alt="Firstgiving" width="200" height="48" className="h-10 sm:h-12 md:h-14 w-auto object-contain mb-6 sm:mb-8" />
            
            {/* Social Icons */}
            <div className="flex gap-3 mb-6 sm:mb-8">
              <a href="https://facebook.com/firstgivinguk" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center hover:opacity-70 transition-opacity">
                <Facebook className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
              </a>
              <a href="https://instagram.com/firstgivinguk" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center hover:opacity-70 transition-opacity">
                <Instagram className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
              </a>
              <a href="https://linkedin.com/company/firstgivinguk" target="_blank" rel="noopener noreferrer" aria-label="Follow us on LinkedIn" className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center hover:opacity-70 transition-opacity">
                <Linkedin className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
              </a>
              <a href="https://twitter.com/firstgivinguk" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Twitter" className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center hover:opacity-70 transition-opacity">
                <Twitter className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
              </a>
            </div>
            
            {/* Contact Button */}
            <Button 
              className="bg-primary hover:bg-primary-hover text-white rounded-full px-6 sm:px-8 h-10 sm:h-12 font-semibold text-sm sm:text-base w-full sm:w-auto"
              onClick={() => navigate('/page/contact-support')}
            >
              Contact Us
            </Button>
          </div>

          {/* For Fundraisers */}
          <div>
            <span className="font-bold text-white mb-4 sm:mb-6 text-sm sm:text-base block">For Fundraisers</span>
            <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-white/80">
              <li><Link to="/create-fundraiser" className="hover:text-white transition-colors">Start a Fundraiser</Link></li>
              <li><Link to="/page/fundraising-tips" className="hover:text-white transition-colors">Fundraising Tips</Link></li>
              <li><Link to="/page/success-stories" className="hover:text-white transition-colors">Success Stories</Link></li>
              <li><Link to="/page/campaign-guidelines" className="hover:text-white transition-colors">Campaign Guidelines</Link></li>
              <li><Link to="/dashboard" className="hover:text-white transition-colors">Fundraiser Dashboard</Link></li>
              <li><Link to="/page/fundraising-ideas" className="hover:text-white transition-colors">Fundraising Ideas</Link></li>
            </ul>
          </div>

          {/* For Organizations */}
          <div>
            <span className="font-bold text-white mb-4 sm:mb-6 text-sm sm:text-base block">For Organizations</span>
            <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-white/80">
              <li><Link to="/auth?type=organization" className="hover:text-white transition-colors">Register Your NGO</Link></li>
              <li><Link to="/dashboard?tab=verification" className="hover:text-white transition-colors">Organization Verification</Link></li>
              <li><Link to="/dashboard" className="hover:text-white transition-colors">Campaign Management</Link></li>
              <li><Link to="/organizations" className="hover:text-white transition-colors">Partner With Us</Link></li>
              <li><Link to="/dashboard?tab=reports" className="hover:text-white transition-colors">Impact Reports</Link></li>
            </ul>
          </div>

          {/* For Donors */}
          <div>
            <span className="font-bold text-white mb-4 sm:mb-6 text-sm sm:text-base block">For Donors</span>
            <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-white/80">
              <li><Link to="/page/how-to-donate" className="hover:text-white transition-colors">How to Donate</Link></li>
              <li><Link to="/explore" className="hover:text-white transition-colors">Browse Campaigns</Link></li>
              <li><Link to="/page/donor-protection" className="hover:text-white transition-colors">Donor Protection</Link></li>
              <li><Link to="/page/tax-benefits" className="hover:text-white transition-colors">Tax Benefits</Link></li>
              <li><Link to="/dashboard" className="hover:text-white transition-colors">Donation History</Link></li>
              <li><Link to="/page/monthly-giving" className="hover:text-white transition-colors">Monthly Giving</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <span className="font-bold text-white mb-4 sm:mb-6 text-sm sm:text-base block">Company</span>
            <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-white/80">
              <li><Link to="/about" className="hover:text-white transition-colors">About FirstGiving</Link></li>
              <li><Link to="/page/our-story" className="hover:text-white transition-colors">Our Story</Link></li>
              <li><Link to="/page/careers" className="hover:text-white transition-colors">Careers</Link></li>
              <li><Link to="/page/press-center" className="hover:text-white transition-colors">Press Center</Link></li>
              <li><Link to="/page/contact-support" className="hover:text-white transition-colors">Contact Support</Link></li>
              <li><Link to="/page/trust-safety" className="hover:text-white transition-colors">Trust & Safety</Link></li>
            </ul>
          </div>

          {/* Resources & Support */}
          <div>
            <span className="font-bold text-white mb-4 sm:mb-6 text-sm sm:text-base block">Resources & Support</span>
            <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-white/80">
              <li><Link to="/page/help-center" className="hover:text-white transition-colors">Help Center</Link></li>
              <li><Link to="/page/faqs" className="hover:text-white transition-colors">FAQs</Link></li>
              <li><Link to="/stories" className="hover:text-white transition-colors">Stories & Articles</Link></li>
              <li><Link to="/page/community-guidelines" className="hover:text-white transition-colors">Community Guidelines</Link></li>
              <li><Link to="/page/platform-fees" className="hover:text-white transition-colors">Platform Fees</Link></li>
              <li><Link to="/page/safety-security" className="hover:text-white transition-colors">Safety & Security</Link></li>
              <li><Link to="/page/transparency-report" className="hover:text-white transition-colors">Transparency Report</Link></li>
            </ul>
          </div>
        </div>

        {/* Certification Logos */}
        <div className="pt-6 sm:pt-8 border-t border-white/20">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 md:gap-10 pb-6 sm:pb-8">
            <a href="https://www.trustpilot.com/review/firstgiving.co.uk" target="_blank" rel="noopener noreferrer">
              <img src={trustpilotLogo} loading="lazy" width="120" height="48" alt="Trustpilot" className="h-10 sm:h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" />
            </a>
            <img src={gdprLogo} loading="lazy" width="120" height="48" alt="GDPR Compliant" className="h-10 sm:h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" />
            <img src={iso9001Logo} loading="lazy" width="120" height="56" alt="ISO 9001:2015" className="h-12 sm:h-14 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" />
            <img src={sectigoLogo} loading="lazy" width="120" height="40" alt="Secured by Sectigo" className="h-9 sm:h-10 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" />
            <img src={bcorpLogo} loading="lazy" width="120" height="64" alt="Certified B Corporation" className="h-14 sm:h-16 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" />
          </div>

          {/* Bottom Legal Section */}
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4 text-xs sm:text-sm text-white/70 pt-6 sm:pt-8 border-t border-white/10">
          <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 justify-center sm:justify-start text-[12px]">
              <Link to="/page/terms-of-use" className="hover:text-white transition-colors text-[12px]">Terms of Use</Link>
              <Link to="/page/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/page/cookie-policy" className="hover:text-white transition-colors">Cookie Policy</Link>
              <Link to="/page/donor-agreement" className="hover:text-white transition-colors">Donor Agreement</Link>
              <Link to="/page/charity-terms" className="hover:text-white transition-colors">Charity Terms</Link>
              <Link to="/page/accessibility" className="hover:text-white transition-colors">Accessibility</Link>
            </div>
            
            <div className="flex gap-3 items-center justify-center sm:justify-end w-full sm:w-auto">
              <img src={accessibilityLogo} loading="lazy" width="32" height="32" alt="Accessibility" className="w-7 h-7 sm:w-8 sm:h-8" />
              <img src={cookieLogo} loading="lazy" width="32" height="32" alt="Cookie Policy" className="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
